import type { Feature } from "geojson";

import { hasCoordinates, isSameCoord, validateGeometry } from "./geometryUtils";

/**
 * Append a coordinate to the end of a feature's coordinate array.
 *
 * @param feature - The GeoJSON feature.
 * @param newCoordinate - The new coordinate to add [lng, lat] or [lng, lat, alt].
 * @returns The feature with the coordinate appended at the end.
 */
export default function appendCoordinate(
  feature: Feature,
  newCoordinate: [number, number] | [number, number, number],
  options: { skipValidation: boolean } = { skipValidation: false },
): Feature {
  // Validate that the feature has a valid geometry with coordinates
  if (!feature.geometry) {
    throw new Error("Feature geometry is null or does not have coordinates");
  }

  // Check for unsupported geometry types first
  if (feature.geometry.type === "GeometryCollection") {
    throw new Error(
      "Cannot add coordinate to GeometryCollection - operation not supported",
    );
  }

  if (feature.geometry.type === "Point") {
    throw new Error(
      "Cannot add coordinate to Point geometry - it would result in an invalid geometry",
    );
  }

  if (!hasCoordinates(feature.geometry)) {
    throw new Error("Feature geometry does not have coordinates");
  }

  let updatedCoordinates: any;

  switch ((feature.geometry as any).type) {
    case "MultiPoint":
      // Add coordinate to the end of the array
      updatedCoordinates = [
        ...(feature.geometry.coordinates as any),
        newCoordinate,
      ];
      break;

    case "LineString":
      // Add coordinate to the end of the array
      updatedCoordinates = [
        ...(feature.geometry.coordinates as any),
        newCoordinate,
      ];
      break;

    case "MultiLineString":
      // Add coordinate to the first line, or create a new line if empty
      if ((feature.geometry.coordinates as any).length === 0) {
        updatedCoordinates = [[newCoordinate]];
      } else {
        const firstLine = [
          ...(feature.geometry.coordinates as any)[0],
          newCoordinate,
        ];
        updatedCoordinates = [
          firstLine,
          ...(feature.geometry.coordinates as any).slice(1),
        ];
      }
      break;

    case "Polygon":
      // Add coordinate to the first ring, or create a new ring if empty
      if ((feature.geometry.coordinates as any).length === 0) {
        // Empty polygon - add coordinate twice for closure
        updatedCoordinates = [[newCoordinate, [...newCoordinate]]];
      } else {
        const firstRing = [...(feature.geometry.coordinates as any)[0]];
        if (firstRing.length === 0) {
          // Empty ring - add coordinate twice for closure
          firstRing.push(newCoordinate, [...newCoordinate]);
        } else {
          const isClosed =
            firstRing.length > 0 &&
            isSameCoord(firstRing[0], firstRing[firstRing.length - 1]);

          if (isClosed) {
            // Insert before the closing coordinate
            firstRing.splice(firstRing.length - 1, 0, newCoordinate);
          } else {
            // Add to the end
            firstRing.push(newCoordinate);
          }
        }

        updatedCoordinates = [
          firstRing,
          ...(feature.geometry.coordinates as any).slice(1),
        ];
      }
      break;

    case "MultiPolygon":
      // Add coordinate to the first ring of the first polygon, or create new if empty
      if ((feature.geometry.coordinates as any).length === 0) {
        // Empty multipolygon - add coordinate twice for closure
        updatedCoordinates = [[[newCoordinate, [...newCoordinate]]]];
      } else {
        const firstPolygon = [...(feature.geometry.coordinates as any)[0]];
        if (firstPolygon.length === 0) {
          // Empty first polygon - add coordinate twice for closure
          firstPolygon.push([newCoordinate, [...newCoordinate]]);
        } else {
          const firstRing = [...firstPolygon[0]];
          if (firstRing.length === 0) {
            // Empty ring - add coordinate twice for closure
            firstRing.push(newCoordinate, [...newCoordinate]);
          } else {
            const isClosed =
              firstRing.length > 0 &&
              isSameCoord(firstRing[0], firstRing[firstRing.length - 1]);

            if (isClosed) {
              // Insert before the closing coordinate
              firstRing.splice(firstRing.length - 1, 0, newCoordinate);
            } else {
              // Add to the end
              firstRing.push(newCoordinate);
            }
          }

          firstPolygon[0] = firstRing;
        }

        updatedCoordinates = [
          firstPolygon,
          ...(feature.geometry.coordinates as any).slice(1),
        ];
      }
      break;

    default:
      throw new Error(
        `Unsupported geometry type: ${(feature.geometry as any).type}`,
      );
  }

  const updatedFeature = {
    ...feature,
    geometry: {
      ...feature.geometry,
      coordinates: updatedCoordinates,
    },
  };

  // Validate the result after modification
  if (!options.skipValidation) {
    validateGeometry(updatedFeature);
  }

  return updatedFeature;
}
